import { useState, useEffect } from 'react';

interface PriceConfig {
  service: { [key: string]: number };
  zone: { [key: string]: number };
  weight: { [key: string]: number };
  urgency: { [key: string]: number };
}

const priceConfig: PriceConfig = {
  service: {
    'coursier-moto': 15,
    'utilitaire': 29,
    'demenagement': 79,
    'livraison-express': 19
  },
  zone: {
    'paris-centre': 1,
    'paris-intramuros': 1.2,
    'banlieue-proche': 1.5,
    'ile-de-france': 2
  },
  weight: {
    'leger': 1,
    'moyen': 1.3,
    'lourd': 1.8,
    'tres-lourd': 2.5
  },
  urgency: {
    'standard': 1,
    'express-1h': 1.5,
    'ultra-express-30min': 2
  }
};

const PriceSimulator = () => {
  const [service, setService] = useState('coursier-moto');
  const [zone, setZone] = useState('paris-centre');
  const [weight, setWeight] = useState('leger');
  const [urgency, setUrgency] = useState('standard');
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showPrice, setShowPrice] = useState(false);

  useEffect(() => {
    const basePrice = priceConfig.service[service];
    const zoneMultiplier = priceConfig.zone[zone];
    const weightMultiplier = priceConfig.weight[weight];
    const urgencyMultiplier = priceConfig.urgency[urgency];

    const finalPrice = Math.round(basePrice * zoneMultiplier * weightMultiplier * urgencyMultiplier);
    setEstimatedPrice(finalPrice);
    setShowPrice(true);
  }, [service, zone, weight, urgency]);

  const handleQuoteRequest = () => {
    const message = `Bonjour, je souhaite obtenir un devis pour : ${service}, zone ${zone}, poids ${weight}, urgence ${urgency}. Prix estimé : ${estimatedPrice}€`;
    window.open(`https://wa.me/33632410246?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-8 shadow-lg animate-fadeIn">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full mb-4">
            <i className="ri-calculator-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Simulateur de Prix
          </h2>
          <p className="text-gray-600 text-lg">
            Estimez le coût de votre transport en quelques clics
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 md:p-8 shadow-md">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Type de service */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <i className="ri-truck-line mr-2 text-teal-600"></i>
                Type de service
              </label>
              <select
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="coursier-moto">Coursier moto</option>
                <option value="utilitaire">Utilitaire</option>
                <option value="demenagement">Déménagement</option>
                <option value="livraison-express">Livraison express</option>
              </select>
            </div>

            {/* Zone */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                Zone de livraison
              </label>
              <select
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="paris-centre">Paris centre</option>
                <option value="paris-intramuros">Paris intra-muros</option>
                <option value="banlieue-proche">Banlieue proche</option>
                <option value="ile-de-france">Île-de-France</option>
              </select>
            </div>

            {/* Poids/Volume */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <i className="ri-weight-line mr-2 text-teal-600"></i>
                Poids / Volume
              </label>
              <select
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="leger">Léger (&lt; 5kg)</option>
                <option value="moyen">Moyen (5-30kg)</option>
                <option value="lourd">Lourd (30-100kg)</option>
                <option value="tres-lourd">Très lourd (100kg+)</option>
              </select>
            </div>

            {/* Urgence */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                <i className="ri-time-line mr-2 text-teal-600"></i>
                Niveau d'urgence
              </label>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm cursor-pointer"
              >
                <option value="standard">Standard</option>
                <option value="express-1h">Express 1h</option>
                <option value="ultra-express-30min">Ultra-express 30min</option>
              </select>
            </div>
          </div>

          {/* Prix estimé */}
          {showPrice && (
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg p-6 mb-6 text-center animate-fadeIn">
              <p className="text-white/90 text-sm font-medium mb-2">Prix estimé</p>
              <div className="flex items-center justify-center">
                <span className="text-5xl font-bold text-white">{estimatedPrice}€</span>
              </div>
              <p className="text-white/80 text-sm mt-2">
                * Prix indicatif, devis précis sur demande
              </p>
            </div>
          )}

          {/* Boutons CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleQuoteRequest}
              className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-6 py-4 rounded-lg font-bold text-lg hover:from-teal-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center"
            >
              <i className="ri-whatsapp-line mr-2 text-xl"></i>
              Demander un devis gratuit
            </button>
            <a
              href="tel:+33632410246"
              className="flex-1 bg-white border-2 border-teal-500 text-teal-600 px-6 py-4 rounded-lg font-bold text-lg hover:bg-teal-50 transition-colors shadow-md whitespace-nowrap cursor-pointer flex items-center justify-center"
            >
              <i className="ri-phone-line mr-2 text-xl"></i>
              +33 6 32 41 02 46
            </a>
          </div>

          {/* Info supplémentaire */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-gray-700 text-center">
              <i className="ri-information-line text-amber-600 mr-2"></i>
              <strong>Service disponible 24h/24, 7j/7</strong> — Réponse immédiate à votre demande
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSimulator;
